import React from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import AutoSizer, { Size } from 'react-virtualized-auto-sizer';
import isEqual from 'lodash.isequal';

// Types
import { AppState } from 'services/types';
import {
    VirtualizedTableColumnKey,
    VirtualizedTableColumnWidth,
    VirtualizedTableColumns,
    VirtualizedTableItem
} from 'services/virtualizedTable/types';
import { updateQueryString } from 'services/router/actions';

// Global Actions
import { setColumnWidth } from 'services/columnWidths/actions';

// Global Components
import VirtualizedTable from 'components/VirtualizedTable';

import { FertilizerEventUuid } from 'services/fertilizerEvents/types';
import { FERTILIZER_EVENT_UPDATE_MODAL } from 'scenes/Fertilizer/constants';
import {
    makeSelectFertilizerEventItems,
    selectFertilizerEventsTableColumns
} from './selectors';

interface OwnProps {
    tableId: string;
}

interface ConnectedProps {
    items: VirtualizedTableItem[];
    columns: VirtualizedTableColumns;
}

interface DispatchProps {
    setColumnWidth: typeof setColumnWidth;
    updateQueryString: typeof updateQueryString;
}

type Props = OwnProps & ConnectedProps & DispatchProps;

export class FertilizerEventsVirtualizedTableComponent extends React.Component<Props> {
    shouldComponentUpdate(nextProps: Props): boolean {
        return !isEqual(this.props, nextProps);
    }

    handleColumnResized = (
        columnKey: VirtualizedTableColumnKey,
        columnWidth: VirtualizedTableColumnWidth
    ): void => {
        const { tableId, ...props } = this.props;
        props.setColumnWidth(tableId, columnKey, columnWidth);
    };

    handleOnExpand = (fertilizerEventUuid: FertilizerEventUuid): void => {
        const { ...props } = this.props;

        props.updateQueryString({
            [FERTILIZER_EVENT_UPDATE_MODAL]: true,
            fertilizerEventUuid
        });
    };

    renderVirtualizedTable = ({ height, width }: Size): React.ReactElement => {
        const { tableId, items, columns } = this.props;

        return (
            <React.Fragment>
                <VirtualizedTable
                    id={tableId}
                    height={height}
                    width={width}
                    columns={columns}
                    items={items}
                    onColumnResized={this.handleColumnResized}
                    labelPlural="Fertilizers"
                    onExpand={this.handleOnExpand}
                    editable
                    selectable
                />
            </React.Fragment>
        );
    };

    render(): React.ReactElement {
        return (
            <AutoSizer>
                {(size: Size): React.ReactElement =>
                    this.renderVirtualizedTable(size)
                }
            </AutoSizer>
        );
    }
}

const mapStateToProps = createStructuredSelector<
    AppState,
    OwnProps,
    ConnectedProps
>({
    items: makeSelectFertilizerEventItems(),
    columns: selectFertilizerEventsTableColumns
});

export default connect(mapStateToProps, {
    setColumnWidth,
    updateQueryString
})(FertilizerEventsVirtualizedTableComponent);
